/**
 * Tacha las credenciales de lo que el logger de gRPC imprime en la consola.
 *
 * Por qué existe: Datadog Logs (`forwardConsoleLogs: 'all'`) y las migas de consola de Sentry
 * serializan los argumentos de `console.log`, así que todo lo que el logger imprime sale del
 * navegador. Se sigue enviando todo, pero sin el token de sesión ni las contraseñas
 * (decisión del CTO del 2026-10-28, BET-773).
 *
 * Es una función pura: devuelve una copia y nunca muta lo que recibe. Debe usarse solo sobre la
 * copia que se imprime (`GrpcMetadata.toObject()`, `toObject()` / `toProtobufJSON()` de los
 * mensajes); la metadata y el mensaje reales de la llamada deben seguir viajando intactos.
 */
export const REDACTED_AUTHORIZATION = 'Bearer ***';
export const REDACTED_VALUE = '***';

/** Solo se recorren objetos planos y listas; `Uint8Array`, `Date`, eventos y clases se dejan tal cual. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function redactEntry(key: string, value: unknown): unknown {
  const lowerKey = key.toLowerCase();
  if (lowerKey === 'authorization') {
    return isEmpty(value) ? value : REDACTED_AUTHORIZATION;
  }
  if (lowerKey.includes('password')) {
    return isEmpty(value) ? value : REDACTED_VALUE;
  }
  return redactGrpcLogValue(value);
}

/**
 * Devuelve una copia de `value` con el header `Authorization` como `"Bearer ***"` y todo campo
 * cuyo nombre contenga `password` como `"***"` (a cualquier profundidad, también dentro de
 * listas). Un valor vacío o `null` se deja como está.
 */
export function redactGrpcLogValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(item => redactGrpcLogValue(item)) as unknown as T;
  }
  if (isPlainObject(value)) {
    const copy: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      copy[key] = redactEntry(key, entry);
    }
    return copy as T;
  }
  return value;
}
